import { ChangeDetectionStrategy, ChangeDetectorRef, Component, input, output } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ButtonComponent } from '@examdojo/core/button';
import { IconComponent } from '@examdojo/core/icon';
import { distinctUntilDeepChanged } from '@examdojo/core/rxjs';
import { QuestionImageFormViewerComponent } from '@examdojo/question-image';
import { MarkdownViewerComponent } from '@examdojo/ui/markdown-viewer';
import { switchMap, tap } from 'rxjs';
import { ContentItemFormArray, ContentItemFormGroup } from './question-content-item.model';
import { QuestionTableFormViewerComponent } from './table-editor/question-table-form-viewer.component';

@Component({
  selector: 'dojo-content-item-viewer',
  imports: [
    MarkdownViewerComponent,
    QuestionImageFormViewerComponent,
    QuestionTableFormViewerComponent,
    ButtonComponent,
    IconComponent,
    MatTooltipModule,
  ],
  template: `
    @for (item of contentItemsFormArray().controls; track $index) {
      @switch (item.value.type) {
        @case ('text') {
          <dojo-markdown-viewer class="w-full [&:has(.katex)]:py-1" [content]="item.value.content ?? ''" />
        }
        @case ('table') {
          <div class="w-full">
            <div class="relative mx-auto py-2" [style]="getContainerStyle(item)">
              <dojo-question-table-form-viewer [formCtrl]="$any(item)" />
              @if (isAllowedToEditTable()) {
                <dojo-button
                  (click)="editItem.emit({ item, contentItemIndex: $index })"
                  class="show-pointer-events absolute right-0 top-[5px]"
                  [size]="'xs'"
                  [fill]="'outline'"
                >
                  <dojo-icon [icon]="'pencil'" class="mr-2" />
                  <span>Edit table</span>
                </dojo-button>
              }
            </div>
          </div>
        }
        @case ('image') {
          <div class="relative p-2" [style]="getContainerStyle(item)">
            <dojo-question-image-form-viewer [formCtrl]="$any(item)" class="show-pointer-events" />
            @if (isAllowedToEditImage()) {
              <dojo-button
                (click)="editItem.emit({ item, contentItemIndex: $index })"
                class="show-pointer-events absolute right-0 top-[10px]"
                [size]="'xs'"
                [fill]="'outline'"
              >
                <dojo-icon [icon]="'pencil'" class="mr-2" />
                <span>Edit image</span>
              </dojo-button>
            }
          </div>
        }
      }
    }
  `,
  host: {
    class: 'w-full h-full flex flex-wrap',
  },
  styles: [
    `
      .badge-above {
        @apply rounded px-2 py-1 text-xs font-medium tracking-wide;
      }

      ::ng-deep .options-container {
        .show-pointer-events {
          pointer-events: all;
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentItemViewerComponent {
  constructor(private readonly cdRef: ChangeDetectorRef) {
    toObservable(this.contentItemsFormArray)
      .pipe(
        switchMap((formCtrl) => formCtrl.valueChanges),
        distinctUntilDeepChanged(),
        tap(() => this.cdRef.markForCheck()),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  readonly contentItemsFormArray = input.required<ContentItemFormArray>();
  readonly isAllowedToEditImage = input<boolean>(false);
  readonly isAllowedToEditTable = input<boolean>(false);
  readonly editItem = output<{ item: ContentItemFormGroup; contentItemIndex: number }>();

  getContainerStyle(item: ContentItemFormGroup): Record<string, string> {
    if (item.value.type !== 'image' && item.value.type !== 'table') {
      return {};
    }

    const containerLayout = item.value.container_width;

    if (containerLayout && containerLayout !== '100%') {
      return { width: containerLayout };
    }

    return { width: '100%' };
  }
}
